// CertificatesPanel.jsx
// ─────────────────────────────────────────────────────────────
// 🎓 Certificates tab — saare certificate types ek jagah
//
// Naya certificate add karna ho to sirf CERT_TYPES me ek entry
// daalo (backend registry se match karke) — baaki UI khud ban jayega.
//
// Backend:
//   POST /api/certificate/<path>/preview   → PDF wapas (mail NAHI)
//   POST /api/certificate/<path>/send      → PDF + email
//   response → { success, message } ya { success:false, message }
// ─────────────────────────────────────────────────────────────
import { useState, useEffect, useRef } from "react";

const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:5000").replace(/\/+$/, "");
const ACCENT = "#ff5421";

// ─────────────────────────────────────────────────────────────
// Project presets — dropdown se project chuno, domain aur
// description khud bhar jayenge. Phir bhi edit kar sakte ho.
// ─────────────────────────────────────────────────────────────
const PROJECT_PRESETS = [
  {
    title: "Pizza Sales Performance & Operational Optimization System",
    domain: "Pizza Sales Performance & Operational Optimization",
    description:
      "This project focuses on analyzing and optimizing pizza sales performance using data analytics and business intelligence techniques. The system evaluates sales trends, customer purchasing behavior, product performance, order patterns, and operational metrics to identify growth opportunities and improve business efficiency. Through interactive dashboards, performance monitoring, and data-driven insights, the project helps enhance inventory management, optimize menu offerings, increase revenue, and support strategic decision-making for restaurant operations.",
  },
  {
    title: "Customer Churn Analysis System",
    domain: "Data Analytics & Business Intelligence",
    description:
      "This project focuses on analyzing customer data to identify trends, predict churn risks, and support customer retention strategies through interactive dashboards and performance reports.",
  },
  {
    title: "Sales Dashboard & Revenue Insights System",
    domain: "Data Analytics & Business Intelligence",
    description:
      "This project focuses on building an end-to-end sales reporting solution that consolidates regional revenue, product-wise performance, and month-on-month growth into a single interactive dashboard. The system highlights top-performing categories, tracks targets against actuals, and surfaces the insights needed for faster, better-informed commercial decisions.",
  },
  {
    title: "HR Attrition Analysis System",
    domain: "People Analytics & Business Intelligence",
    description:
      "This project analyzes employee records to understand the drivers behind attrition across departments, roles, and experience bands. Using statistical analysis and visual reporting, the system identifies high-risk segments, quantifies the cost of turnover, and recommends targeted retention measures for the HR team.",
  },
  {
    title: "Inventory & Supply Chain Optimization System",
    domain: "Operations Analytics & Business Intelligence",
    description:
      "This project focuses on optimizing stock levels and supplier performance through demand forecasting and reorder-point analysis. The system tracks fast- and slow-moving inventory, flags stock-out risks in advance, and provides dashboards that help reduce holding costs while maintaining service levels.",
  },
];

// ─────────────────────────────────────────────────────────────
// Certificate types — backend registry ke saath match karta hai
// ─────────────────────────────────────────────────────────────
const CERT_TYPES = [
  {
    key: "gvcloud-project",
    tab: "☁️ GV Cloud",
    title: "Certificate of Project Completion",
    sub: "GV Cloud — intern / project completion",
    path: "gvcloud/project-completion",
    fixedNote: "Manager: Mr. Rahul Kumar · GvCloud Secure Pvt. Ltd. (backend me fixed hai)",
    usePresets: true,
    fields: [
      { key: "name", label: "Student / Intern Name", placeholder: "e.g. Aryan Harshit" },
      { key: "date", label: "Project Completion Date", type: "date" },
      {
        key: "projectTitle",
        label: "Project Title",
        full: true,
        placeholder: "e.g. Pizza Sales Performance & Operational Optimization System",
        hint: "Certificate ke body text me bold aayega",
      },
      {
        key: "projectDomain",
        label: "Project Domain",
        full: true,
        placeholder: "e.g. Pizza Sales Performance & Operational Optimization",
      },
      {
        key: "projectDescription",
        label: "Project Description",
        type: "textarea",
        full: true,
        hint: "Enter dabane se naye line ban jayegi",
      },
    ],
  },
  {
    key: "clouddetox-project",
    tab: "🧊 Cloud Detox",
    title: "Certificate of Project Completion",
    sub: "Cloud Detox — intern / project completion",
    path: "clouddetox/project-completion",
    fixedNote: "Manager: Mr. Satya Shah · Cloud Detox (backend me fixed hai)",
    usePresets: true,
    fields: [
      { key: "name", label: "Student / Intern Name", placeholder: "e.g. Aryan Sharma" },
      { key: "date", label: "Project Completion Date", type: "date" },
      {
        key: "projectTitle",
        label: "Project Title",
        full: true,
        placeholder: "e.g. Customer Churn Analysis System",
        hint: "Certificate me quotes ke andar bold aayega",
      },
      {
        key: "projectDomain",
        label: "Project Domain",
        full: true,
        placeholder: "e.g. Data Analytics & Business Intelligence",
      },
      {
        key: "projectDescription",
        label: "Project Description",
        type: "textarea",
        full: true,
        hint: "Enter dabane se naye line ban jayegi",
      },
    ],
  },
  {
    key: "uncodemy-agile",
    tab: "🎓 Uncodemy Agile",
    title: "Certificate of Participation",
    sub: "Uncodemy — Agile Activity session",
    path: "uncodemy/agile-activity",
    fixedNote: "Venue, description aur dono signatory backend me fixed hain",
    usePresets: false,
    fields: [
      { key: "name", label: "Student Name", placeholder: "e.g. Aditi Singh" },
      { key: "date", label: "Session Date", type: "date" },
      {
        key: "course",
        label: "Course / Topic",
        full: true,
        placeholder: "e.g. Data Analytics",
        hint: "Certificate ke bold orange fill me aayega",
      },
    ],
  },
];

// Har type ka khaali form bana lo (email + saare fields)
function emptyForms() {
  const out = {};
  for (const t of CERT_TYPES) {
    out[t.key] = { email: "" };
    for (const f of t.fields) out[t.key][f.key] = "";
  }
  return out;
}

export default function CertificatesPanel({ adminKey }) {
  const [activeKey, setActiveKey] = useState(CERT_TYPES[0].key);
  const [forms, setForms] = useState(emptyForms);
  const [busy, setBusy] = useState(null); // 'preview' | 'send' | null
  const [result, setResult] = useState(null); // { ok, msg }
  const [previewUrl, setPreviewUrl] = useState(null);

  const previewUrlRef = useRef(null);

  const type = CERT_TYPES.find((t) => t.key === activeKey);
  const form = forms[activeKey];

  // Component hatne par blob URL free kar do
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  function setField(key, value) {
    setForms((prev) => ({
      ...prev,
      [activeKey]: { ...prev[activeKey], [key]: value },
    }));
    setResult(null);
  }

  function applyPreset(index) {
    if (index === "") return;
    const p = PROJECT_PRESETS[Number(index)];
    if (!p) return;
    setForms((prev) => ({
      ...prev,
      [activeKey]: {
        ...prev[activeKey],
        projectTitle: p.title,
        projectDomain: p.domain,
        projectDescription: p.description,
      },
    }));
    setResult(null);
  }

  function resetForm() {
    setForms((prev) => ({ ...prev, [activeKey]: emptyForms()[activeKey] }));
    setResult(null);
    closePreview();
  }

  function switchTab(key) {
    setActiveKey(key);
    setResult(null);
    closePreview();
  }

  function closePreview() {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
  }

  function headers() {
    return {
      "Content-Type": "application/json",
      ...(adminKey ? { "x-admin-key": adminKey } : {}),
    };
  }

  // ── Validation ────────────────────────────────────────────
  function validate({ needEmail }) {
    const has = (v) => v != null && String(v).trim() !== "";

    for (const f of type.fields) {
      if (!has(form[f.key])) return `${f.label} bharna zaroori hai`;
    }
    if (needEmail) {
      if (!has(form.email)) return "Email bharna zaroori hai";
      if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return "Email sahi format me nahi hai";
    }
    return null;
  }

  // Backend ko sirf wahi keys jaati hain jo type me defined hain
  function buildPayload({ withEmail }) {
    const payload = {};
    for (const f of type.fields) payload[f.key] = String(form[f.key]).trim();
    if (withEmail) payload.toEmail = form.email.trim();
    return payload;
  }

  // ── Preview PDF (mail nahi jata) ──────────────────────────
  async function handlePreview() {
    const err = validate({ needEmail: false });
    if (err) return setResult({ ok: false, msg: err });

    setBusy("preview");
    setResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/certificate/${type.path}/preview`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify(buildPayload({ withEmail: false })),
      });

      if (!res.ok) {
        // PDF ke bajaye JSON error aaya hai
        let msg = `Preview fail (HTTP ${res.status})`;
        try {
          const data = await res.json();
          msg = data.message || data.error || msg;
        } catch {
          /* JSON bhi nahi hai */
        }
        setResult({ ok: false, msg });
        return;
      }

      const blob = await res.blob();
      closePreview();
      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch {
      setResult({ ok: false, msg: "Server se connect nahi ho pa raha" });
    } finally {
      setBusy(null);
    }
  }

  // ── Send (PDF + email) ────────────────────────────────────
  async function handleSend() {
    const err = validate({ needEmail: true });
    if (err) return setResult({ ok: false, msg: err });

    const ok = window.confirm(
      `${type.title}\n\n${form.name}\n${form.email} par bhejein?`
    );
    if (!ok) return;

    setBusy("send");
    setResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/certificate/${type.path}/send`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify(buildPayload({ withEmail: true })),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        /* JSON nahi aaya */
      }

      if (res.ok && data.success) {
        setResult({ ok: true, msg: data.message || "Certificate bhej diya gaya!" });
      } else {
        setResult({
          ok: false,
          msg: data.message || data.error || `Send fail ho gaya (HTTP ${res.status})`,
        });
      }
    } catch {
      setResult({ ok: false, msg: "Server se connect nahi ho pa raha" });
    } finally {
      setBusy(null);
    }
  }

  const isBusy = busy !== null;

  // Full-width fields aur half-width fields alag kar lo
  const halfFields = type.fields.filter((f) => !f.full);
  const fullFields = type.fields.filter((f) => f.full);

  return (
    <div>
      {/* ── Info banner ── */}
      <div style={s.infoBar}>
        🎓 Certificate select karo, details bharo, <b>Preview</b> se check karo, phir{" "}
        <b>Send</b> karo. Preview par koi mail nahi jata.
      </div>

      {/* ── Type tabs ── */}
      <div style={s.tabRow}>
        {CERT_TYPES.map((t) => {
          const active = t.key === activeKey;
          return (
            <button
              key={t.key}
              onClick={() => switchTab(t.key)}
              disabled={isBusy}
              style={{ ...s.tab, ...(active ? s.tabActive : {}) }}
            >
              {t.tab}
            </button>
          );
        })}
      </div>

      {/* ── Form card ── */}
      <div style={{ ...s.formCard, borderTop: `3px solid ${ACCENT}` }}>
        <div style={s.formHeader}>
          <span style={{ fontSize: 22 }}>{type.tab.split(" ")[0]}</span>
          <div style={{ flex: 1 }}>
            <div style={s.formTitle}>{type.title}</div>
            <div style={s.formSub}>{type.sub}</div>
          </div>
        </div>

        {/* Recipient email */}
        <div style={s.grid}>
          <Field label="Recipient Email *" hint="Isi address par PDF attach hoke jayega">
            <input
              style={s.input}
              type="email"
              placeholder="student@email.com"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
            />
          </Field>
        </div>

        {/* Project preset dropdown */}
        {type.usePresets && (
          <div style={s.presetBox}>
            <label style={s.label}>⚡ Project preset se bharo</label>
            <select
              style={s.select}
              defaultValue=""
              onChange={(e) => {
                applyPreset(e.target.value);
                e.target.value = "";
              }}
            >
              <option value="">— Project chuno (title + domain + description bhar jayenge) —</option>
              {PROJECT_PRESETS.map((p, i) => (
                <option key={p.title} value={i}>
                  {p.title}
                </option>
              ))}
            </select>
            <div style={s.presetHint}>
              Bharne ke baad teeno fields edit kar sakte ho. Naya project ho to seedha neeche
              type kar lo.
            </div>
          </div>
        )}

        {/* Half-width fields (name, date) */}
        {halfFields.length > 0 && (
          <div style={s.grid}>
            {halfFields.map((f) => (
              <Field key={f.key} label={`${f.label} *`} hint={f.hint}>
                <input
                  style={s.input}
                  type={f.type === "date" ? "date" : "text"}
                  placeholder={f.placeholder}
                  value={form[f.key]}
                  onChange={(e) => setField(f.key, e.target.value)}
                />
              </Field>
            ))}
          </div>
        )}

        {/* Full-width fields (project title / domain / description) */}
        {fullFields.map((f) => (
          <Field key={f.key} label={`${f.label} *`} hint={f.hint}>
            {f.type === "textarea" ? (
              <textarea
                style={s.textarea}
                placeholder={f.placeholder}
                value={form[f.key]}
                onChange={(e) => setField(f.key, e.target.value)}
              />
            ) : (
              <input
                style={s.input}
                placeholder={f.placeholder}
                value={form[f.key]}
                onChange={(e) => setField(f.key, e.target.value)}
              />
            )}
          </Field>
        ))}

        {/* Fixed values note */}
        <div style={s.fixedNote}>🔒 {type.fixedNote}</div>

        {/* Result banner */}
        {result && (
          <div style={{ ...s.resultBanner, ...(result.ok ? s.resultOk : s.resultErr) }}>
            {result.ok ? "✅" : "❌"} {result.msg}
          </div>
        )}

        {/* Actions */}
        <div style={s.actionRow}>
          <button style={s.btnReset} onClick={resetForm} disabled={isBusy}>
            Reset
          </button>
          <button
            style={{ ...s.btnPreview, ...(busy === "preview" ? { opacity: 0.7 } : {}) }}
            onClick={handlePreview}
            disabled={isBusy}
          >
            {busy === "preview" ? "⏳ Ban raha hai..." : "👁 Preview PDF"}
          </button>
          <button
            style={{ ...s.btnSend, ...(busy === "send" ? { opacity: 0.7 } : {}) }}
            onClick={handleSend}
            disabled={isBusy}
          >
            {busy === "send" ? "⏳ Generating & sending..." : "📧 Send Certificate"}
          </button>
        </div>

        <div style={s.tip}>
          💡 Date ko backend khud format kar deta hai — <code>2025-12-10</code> chuno,
          certificate par <b>10th December 2025</b> aayega.
        </div>
      </div>

      {/* ── PDF Preview Modal ── */}
      {previewUrl && (
        <div style={s.modalOverlay} onClick={closePreview}>
          <div style={s.previewCard} onClick={(e) => e.stopPropagation()}>
            <div style={s.previewHeader}>
              <div>
                <div style={s.previewTitle}>
                  {type.title} — {form.name || "Preview"}
                </div>
                <div style={s.previewSub}>Ye sirf preview hai, mail nahi gaya</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <a
                  href={previewUrl}
                  download={`${(form.name || "certificate").replace(/\s+/g, "_")}-${type.key}.pdf`}
                  style={s.btnDownload}
                >
                  ⬇ Download
                </a>
                <button style={s.btnClosePreview} onClick={closePreview}>
                  ✕ Close
                </button>
              </div>
            </div>
            <iframe title="PDF Preview" src={previewUrl} style={s.previewFrame} />
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
      <label style={s.label}>{label}</label>
      {children}
      {hint && <span style={s.hint}>{hint}</span>}
    </div>
  );
}

// ── Styles (admin panel theme se match) ─────────────────────
const s = {
  infoBar: {
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    color: "#9a3412",
    borderRadius: 10,
    padding: "12px 16px",
    fontSize: 13,
    marginBottom: 14,
  },

  tabRow: { display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" },
  tab: {
    padding: "10px 18px",
    background: "#fff",
    color: "#475569",
    border: "1.5px solid #e2e8f0",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  tabActive: {
    background: ACCENT,
    color: "#fff",
    border: `1.5px solid ${ACCENT}`,
  },

  formCard: {
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    padding: "22px 24px",
  },
  formHeader: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    marginBottom: 18,
    paddingBottom: 14,
    borderBottom: "1px solid #f1f5f9",
  },
  formTitle: { fontSize: 17, fontWeight: 700, color: "#1e1b4b" },
  formSub: { fontSize: 12, color: "#64748b", marginTop: 2 },

  grid: { display: "flex", gap: 14, marginBottom: 4, flexWrap: "wrap" },

  label: { fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 },
  hint: { fontSize: 11, color: "#94a3b8", marginTop: -8, marginBottom: 10 },
  input: {
    width: "100%",
    padding: "11px 13px",
    borderRadius: 10,
    border: "1.5px solid #e2e8f0",
    fontSize: 14,
    marginBottom: 12,
    boxSizing: "border-box",
    outline: "none",
    fontFamily: "inherit",
  },
  textarea: {
    width: "100%",
    minHeight: 120,
    padding: "11px 13px",
    borderRadius: 10,
    border: "1.5px solid #e2e8f0",
    fontSize: 14,
    lineHeight: 1.6,
    marginBottom: 12,
    boxSizing: "border-box",
    outline: "none",
    fontFamily: "inherit",
    resize: "vertical",
  },
  select: {
    width: "100%",
    padding: "11px 13px",
    borderRadius: 10,
    border: "1.5px solid #e2e8f0",
    fontSize: 14,
    boxSizing: "border-box",
    outline: "none",
    fontFamily: "inherit",
    background: "#fff",
    cursor: "pointer",
  },

  presetBox: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "14px 16px",
    marginBottom: 16,
  },
  presetHint: { fontSize: 11, color: "#94a3b8", marginTop: 8, lineHeight: 1.5 },

  fixedNote: {
    fontSize: 12,
    color: "#64748b",
    background: "#f8fafc",
    border: "1px solid #f1f5f9",
    borderRadius: 8,
    padding: "10px 12px",
    marginTop: 4,
    marginBottom: 4,
  },

  resultBanner: {
    borderRadius: 10,
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 600,
    marginTop: 10,
    marginBottom: 4,
  },
  resultOk: { background: "#d1fae5", border: "1px solid #6ee7b7", color: "#065f46" },
  resultErr: { background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626" },

  actionRow: { display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" },
  btnReset: {
    padding: "12px 24px",
    background: "#f1f5f9",
    color: "#475569",
    border: "none",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  btnPreview: {
    padding: "12px 24px",
    background: "#fff",
    color: ACCENT,
    border: `1.5px solid ${ACCENT}`,
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  btnSend: {
    flex: 1,
    minWidth: 200,
    padding: "12px",
    color: "#fff",
    background: ACCENT,
    border: "none",
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  tip: { fontSize: 12, color: "#94a3b8", marginTop: 12, lineHeight: 1.6 },

  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    padding: 16,
  },
  previewCard: {
    background: "#fff",
    borderRadius: 14,
    width: 900,
    maxWidth: "100%",
    height: "90vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
  },
  previewHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    borderBottom: "1px solid #e2e8f0",
    gap: 12,
  },
  previewTitle: { fontSize: 16, fontWeight: 700, color: "#1e1b4b" },
  previewSub: { fontSize: 12, color: "#64748b", marginTop: 2 },
  previewFrame: { flex: 1, width: "100%", border: "none" },
  btnDownload: {
    color: "#fff",
    background: ACCENT,
    padding: "9px 18px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    textDecoration: "none",
    whiteSpace: "nowrap",
  },
  btnClosePreview: {
    background: "#f1f5f9",
    color: "#475569",
    border: "none",
    padding: "9px 18px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontFamily: "inherit",
  },
};